"use client";
import { useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useState } from "react";


import MDBox from "@/components/atoms/MDBox";
import { setMiniSidenav, useMaterialUIController } from "@/contexts/mui-context";
import { SidebarMessages } from "@/messages/layout/sidebar";
import DashboardNavbar from "@/mui/DashboardNavbar";
import Footer from "@/mui/Footer";
import Sidenav from "@/mui/Sidenav";
import routes from "@/routes";
import { BreadCrumb } from "@/types/page.types";

import { OverduePlanBanner } from "./components/overduePlanBanner";

type Props = {
  ignoreSideBar?: boolean;
  fullscreen?: boolean;
  title?: string;
  breadCrumbs?: BreadCrumb[];
  messages: SidebarMessages;
  hideBoldTitle?: boolean;
} & React.ComponentProps<typeof Grid>;

const PageLayout = ({
  children,
  ignoreSideBar = false,
  fullscreen = false,
  breadCrumbs,
  title,
  messages,
  hideBoldTitle,
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const hideMobileExtras = isMobile;
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, sidenavColor } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  if (fullscreen) {
    return (
      <MDBox width="100%" minHeight="var(--full-vh)" p={{ xs: 1.5, md: 3 }}>
        {children}
      </MDBox>
    );
  }

  if (ignoreSideBar) {
    return (
      <MDBox width="100%" height="var(--full-vh)" mx="auto">
        <Grid container justifyContent="center" alignItems="center" height="100%">
          <Grid maxWidth={416} size={{ xs: 11, sm: 9, md: 5, lg: 4, xl: 3 }}>
            {children}
          </Grid>
        </Grid>
      </MDBox>
    );
  }

  return (
    <>
      <MDBox
        sx={({ breakpoints, transitions, functions: { pxToRem } }) => {
          const marginLeftValue = miniSidenav ? pxToRem(120) : pxToRem(274);
          return {
            p: hideMobileExtras ? 1 : 3,
            pb: 0,
            position: "relative",

            [breakpoints.up("xl")]: {
              marginLeft: marginLeftValue,
              transition: transitions.create(["margin-left", "margin-right"], {
                easing: transitions.easing.easeInOut,
                duration: transitions.duration.standard,
              }),
            },
          };
        }}
      >
        <Sidenav
          color={sidenavColor}
          routes={routes}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
        <DashboardNavbar
          breadCrumbs={breadCrumbs}
          title={title || ""}
          messages={messages}
          hideBoldTitle={hideBoldTitle}
        />
        {!hideMobileExtras && <OverduePlanBanner messages={messages} />}

        {children}
      </MDBox>
      <MDBox
        sx={({ breakpoints, transitions, functions: { pxToRem } }) => {
          const marginLeftValue = miniSidenav ? pxToRem(120) : pxToRem(274);
          return {
            px: isMobile ? 1 : 3,
            pt: isMobile ? 0.5 : 1,
            pb: isMobile ? 1 : 2,
            [breakpoints.up("xl")]: {
              marginLeft: marginLeftValue,
              transition: transitions.create(["margin-left", "margin-right"], {
                easing: transitions.easing.easeInOut,
                duration: transitions.duration.standard,
              }),
            },
          };
        }}
      >
        <Footer messages={messages} compact={isMobile} />
      </MDBox>
    </>
  );
};

export default PageLayout;
